import * as React from 'react';

import S from './StyledButton.styled';
import Icon from '../Icon';
import { ILink } from '@/src/types';
import { ButtonLabel } from '@/styles/typography';

type Props = {
  label?: string;
  url?: string;
  link?: ILink;
  onClick?: () => void;
  color?: 'white' | 'black';
  style?: 'outlined' | 'solid';
  disabled?: boolean;
  noArrow?: boolean;
  center?: boolean;
  fullWidth?: boolean;
  mobileFullWidth?: boolean;
  margin?: string | number;
};

const StyledButton: React.FC<Props> = ({
  label,
  url,
  link,
  onClick,
  style,
  color,
  disabled,
  noArrow,
  margin,
  center,
  fullWidth,
  mobileFullWidth,
}) => {
  const outlined = style === 'outlined';
  const textColor = outlined ? color : color === 'white' ? 'black' : 'white';

  const commonProps = {
    $disabled: disabled,
    $outlined: outlined,
    $color: color,
    $margin: margin,
    $fullWidth: fullWidth,
    $mobileFullWidth: mobileFullWidth,
    $center: center,
  };

  const renderContent = (
    <>
      <ButtonLabel $color={textColor}>{label}</ButtonLabel>
      {!noArrow && (
        <Icon
          src="ArrowRight"
          width={15}
          color={textColor}
          margin="0 0 0 7px"
        />
      )}
    </>
  );

  if (link && link.url)
    return (
      <S.ExternalLink href={link.url} target={link.target} {...commonProps}>
        {renderContent}
      </S.ExternalLink>
    );

  if (link && link.cached_url) {
    const url = `/${link.cached_url.replace(/^\//, '')}${link.anchor ? `/#${link.anchor}` : ''}`;
    return (
      <S.InternalLink to={url} target={link.target} {...commonProps}>
        {renderContent}
      </S.InternalLink>
    );
  }

  if (url)
    return (
      <S.InternalLink to={url} {...commonProps} target={link?.target}>
        {renderContent}
      </S.InternalLink>
    );

  if (onClick)
    return (
      <S.Button onClick={onClick} {...commonProps} tabIndex={0}>
        {renderContent}
      </S.Button>
    );

  if (label)
    return (
      <S.Button {...commonProps} tabIndex={0}>
        {renderContent}
      </S.Button>
    );

  return null;
};

export default StyledButton;
